import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { remove } from 'lodash';
import { EMPTY as empty, Observable, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ListSummaryGroupings } from '../../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import { TValidSchoolTypes } from '../../../../shared/typings/interfaces/school.interface';
import { IUser } from '../../../../shared/typings/interfaces/user.interface';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../../../store/actions/batch-actions-actions';
import { ListSearchComponent } from '../../list-search/list-search.component';
import { LIST_TYPE } from './../../../../shared/constants/list-view/list-view.constant';
import { ImUser } from './../../../../shared/services/im-models/im-user';
import { MadLibGroupings } from './../../../../shared/services/mad-lib-services/mad-lib-groupings';
import { IMadlibOptions, MadLibHelpers } from './../../../../shared/services/mad-lib-services/mad-lib-helpers';
import { MadLibSorting } from './../../../../shared/services/mad-lib-services/mad-lib-sorting';
import { IListViewData } from './../../../../shared/typings/interfaces/list-view.interface';
import { DIMENSION_1, DIMENSION_2 } from './my-students-mad-lib.constant';
import { IMyStudentsDimension, IMyStudentsDimensionCol, TMyStudentsDimension } from './my-students-mad-lib.interface';

@Component({
  selector: 'my-students-mad-lib',
  template: require('./my-students-mad-lib.component.html'),
})
export class MyStudentsMadLib implements OnInit, OnDestroy {
  schoolType: TValidSchoolTypes;
  school;
  schoolId: string;
  selectedItem: string;
  myStudentsMadLibs;
  filteredMyStudentsMadLibs;
  madLibModels; // object each key being a dimension or grouping
  filteredStudents$: Observable<any[]>;
  columns; // object where each key is a number indicating the column order
  groupings$: Observable<any>;
  sortedGroupings$;
  dimension1;
  dimension2;
  selectedDimension1;
  selectedDimension2;
  dim1Paths: string;
  madlibOptions: IMadlibOptions;
  hybridMadlibOptions;
  batchEditMode: boolean;
  searchQuery: string;
  setPanelShadowStyle: boolean = false;

  sortState = { sortedColumn: null, sortOrder: null, dataType: null };
  destroy$: Subject<boolean> = new Subject<boolean>();
  dim1SelectedOption;
  prevSearch: string;
  madlibStateSubscription: ISubscription;

  @Input() currentUser: IUser;
  @Input() madlibOptions$: Observable<any>;
  @Input() batchActionsMode$: Observable<any>;
  @Input() students$;
  @Input() studentsWithFlags$;
  @Input() madlibState$;
  @Input() currentSchool;

  @ViewChild(ListSearchComponent) searchComponent: ListSearchComponent;

  constructor(
    private madLibHelpers: MadLibHelpers,
    private madLibGroupings: MadLibGroupings,
    private madLibSorting: MadLibSorting,
    private store: Store<any>,
    private listSummaryGroupings: ListSummaryGroupings,
    @Inject('$location') public $location: any,
    private imUser: ImUser,
  ) {
    this.dimension2 = DIMENSION_2;
    this.selectedDimension1 = '';
    this.selectedDimension2 = '';
  }

  ngOnInit(): void {
    this.dimension1 = DIMENSION_1;
    this.schoolType = this.currentSchool.schoolType;

    this.studentsWithFlags$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
      )
      .subscribe(() => this.updateGroupings());

    this.initializeInitialLoad();
  }

  // need to unsubscribe from event stream when leaving view
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  // todo: refactor search, sort and madlib methods (dvn)
  search(searchTerm) {
    const formattedSearchTerm = this.madLibHelpers.formatListSearchTerm(searchTerm);
    this.searchQuery = formattedSearchTerm;

    let searchStudents$ = this.madLibHelpers.getFilteredStudents(this.studentsWithFlags$, this.madLibModels.Dimension1);
    searchStudents$ = this.madLibHelpers.applySearchFilter(searchStudents$, formattedSearchTerm);
    this.groupings$ = this.groupStudents(searchStudents$, this.madLibModels, this.columns);

    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true,
      null,
      this.madLibModels,
    );

    this.clearBatchEditsInProgress();
    this.madLibHelpers.updateUrl(this.madLibModels, {
      sortKey,
      dataType,
      orderBy,
      search: this.searchQuery,
      list: LIST_TYPE.MY_STUDENTS,
    });
  }

  clearSearch(): void {
    this.searchComponent.clearSearch();
  }

  // todo: refactor search, sort and madlib methods (dvn)
  sort(newSort) {
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(newSort, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.selectedItem = this.madLibModels.Dimension1.key;
    this.clearBatchEditsInProgress();
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
  }

  // todo: refactor search, sort, madlib and run methods (dvn)
  madLibChange(newDimension, newDimensionNum: TMyStudentsDimension) {
    this.updateListDataOnDimensionChange(newDimension, newDimensionNum);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, newDimension, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.selectedItem = newDimension ? newDimension.key : this.madLibModels.Dimension1.key;
    this.clearBatchEditsInProgress();
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
  }

  initializeInitialLoad(): void {
    // TODO update subscription (JE)

    this.madlibStateSubscription = this.madlibState$.pipe(take(1)).subscribe(madlibState => {
      this.filteredMyStudentsMadLibs = madlibState.filteredMadLib;
      this.madLibModels = madlibState.madLibModel;
    });
    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filterColumnsBySchoolType(this.schoolType);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlags$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.groupStudents(this.filteredStudents$, this.madLibModels, this.columns);

    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.selectedItem = this.madLibModels.Dimension1.key;
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
    const urlValues = this.$location.search();
    this.prevSearch = this.madLibHelpers.getSearchTerm(urlValues, LIST_TYPE.MY_STUDENTS);
  }

  updateGroupings(): void {
    this.batchActionsMode$
      .pipe(
        switchMap(mode => {
          return mode ? empty : this.madlibState$;
        }),
        tap((madlibState: any) => {
          if (madlibState) {
            this.filteredMyStudentsMadLibs = madlibState.filteredMadLib;
            this.madLibModels = this.madLibModels;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.filteredStudents$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.groupStudents(this.filteredStudents$, this.madLibModels, this.columns);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(true, true, validColumns);
    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );
  }

  updateListDataOnDimensionChange(newDimension, newDimensionNum: TMyStudentsDimension): void {
    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };
    this.madLibModels = this.madLibHelpers.updateMadLibModel(this.madLibModels, newDimensionNum, newDimension);
    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filterColumnsBySchoolType(this.schoolType);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlags$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.groupStudents(this.filteredStudents$, this.madLibModels, this.columns);
  }

  // TODO: We should structure this in the same way that the academic-list works
  // leveraging an interdependency between the dimension selections(JJ)
  filterColumnsBySchoolType(type: TValidSchoolTypes) {
    if (type === 'K to 5' || type === 'K to 8' || type === '6 to 8') {
      remove(this.columns, (col: any) => {
        return col.human === 'POST-SECONDARY';
      });
    }
  }

  getValidColumns(columns: IMyStudentsDimensionCol[]): string[] {
    return columns.map(({ key }) => key);
  }

  /* istanbul ignore next: todo: this needs to be moved to the groupings service & tested */
  groupStudents(students$, madLibModels, columns: IMyStudentsDimensionCol[]): Observable<Partial<IListViewData>> {
    const { Dimension2 } = madLibModels;
    let groupings$: Observable<Partial<IListViewData>>;
    switch (Dimension2.key) {
      case 'FLAGS':
        groupings$ = this.madLibGroupings.getHasFlagsGroupings(students$, madLibModels, columns, LIST_TYPE.MY_STUDENTS);
        break;
      case 'POINT_PEOPLE':
        groupings$ = this.madLibGroupings.getHasPointPeopleGroupings(
          students$,
          madLibModels,
          columns,
          LIST_TYPE.MY_STUDENTS,
        );
        break;
      default:
        groupings$ = null;
        break;
    }
    this.listSummaryGroupings.setGroupings(madLibModels, groupings$);
    return groupings$;
  }

  isOptionActive(lib: IMyStudentsDimension, option: string): boolean {
    if (lib.madLib === 'Dimension1') return this.selectedDimension1 === option;
    return this.selectedDimension2 === option;
  }

  // candidate to move to helper
  clearBatchEditsInProgress(): void {
    this.batchEditMode = false;
    this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }
}
